import React, { useState, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog } from "@fortawesome/free-solid-svg-icons";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import CustomSlider from "./CustomSlider";

const TraceToolsPopover = ({
  smoothness,
  setSmoothness,
  despeckle,
  setDespeckle,
  simplifyCurves,
  setSimplifyCurves,
  imageDimensions,
  useSuperpixels,
  setUseSuperpixels,
  useMorphology,
  setUseMorphology,
  topBarStyle,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  // Callback ref: position the panel via direct DOM mutation (no re-render)
  const panelCallbackRef = useCallback((node) => {
    panelRef.current = node;
    if (!node || !triggerRef.current) return;
    const trigger = triggerRef.current.getBoundingClientRect();
    const panelWidth = node.offsetWidth;
    const pad = 12;
    let left = trigger.left + trigger.width / 2 - panelWidth / 2;
    if (left < pad) left = pad;
    if (left + panelWidth > window.innerWidth - pad) left = window.innerWidth - pad - panelWidth;
    node.style.top = (trigger.bottom + 8) + "px";
    node.style.left = left + "px";
    node.style.opacity = "1";
  }, []);

  return (
    <div style={{ position: "relative" }}>
      <button
        ref={triggerRef}
        style={topBarStyle ? {
          display: "flex",
          alignItems: "center",
          gap: "4px",
          background: "rgba(255,255,255,0.1)",
          border: "none",
          fontFamily: "Clash Grotesk",
          fontSize: "10px",
          fontWeight: 600,
          color: "rgba(255,255,255,0.7)",
          cursor: "pointer",
          padding: "0 8px",
          borderRadius: "6px",
          transition: "all 0.15s",
          whiteSpace: "nowrap",
          height: "26px",
        } : {
          display: "flex",
          alignItems: "center",
          gap: "5px",
          background: "transparent",
          border: "none",
          fontFamily: "Clash Grotesk",
          fontSize: "12px",
          fontWeight: 500,
          color: "#475569",
          cursor: "pointer",
          padding: "4px 8px",
          borderRadius: "6px",
          transition: "all 0.15s",
          whiteSpace: "nowrap",
        }}
        onClick={() => setIsOpen((o) => !o)}
        onMouseEnter={topBarStyle ? (e) => { e.currentTarget.style.background = "rgba(255,255,255,0.2)"; } : undefined}
        onMouseLeave={topBarStyle ? (e) => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; } : undefined}
        className={topBarStyle ? undefined : "hover-grey"}
      >
        <FontAwesomeIcon icon={faCog} style={{ fontSize: "13px", opacity: topBarStyle ? 0.8 : 0.6 }} />
        Settings
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 999,
            }}
          />
          {/* Panel */}
          <div
            ref={panelCallbackRef}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              opacity: 0,
              width: "300px",
              maxWidth: "90vw",
              backgroundColor: "#fff",
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)",
              padding: "16px 18px",
              zIndex: 1000,
              transition: "opacity 0.15s ease",
            }}
          >
            {/* Header */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "14px",
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}>
                <FontAwesomeIcon icon={faCog} style={{ fontSize: "13px", color: "#94a3b8" }} />
                <span style={{
                  fontFamily: "Clash Grotesk",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#1e293b",
                }}>
                  Trace Settings
                </span>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: "none",
                  border: "none",
                  padding: "4px",
                  cursor: "pointer",
                  color: "#94a3b8",
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "6px",
                  transition: "color 0.15s",
                }}
              >
                <FontAwesomeIcon icon={faXmark} style={{ fontSize: "15px" }} />
              </button>
            </div>

            {/* Algorithm selector */}
            <div style={{ marginBottom: "12px" }}>
              <div style={{
                fontFamily: "Clash Grotesk",
                fontSize: "11px",
                fontWeight: 500,
                color: "#64748b",
                marginBottom: "6px",
              }}>
                Algorithm
              </div>
              <div style={{
                display: "flex",
                gap: "4px",
                backgroundColor: "#f1f3f5",
                borderRadius: "8px",
                padding: "3px",
              }}>
                <button
                  onClick={() => {
                    setUseSuperpixels(true);
                    if (window.redetectColorsForAlgorithm) {
                      window.redetectColorsForAlgorithm(true);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: "6px 10px",
                    borderRadius: "6px",
                    border: "none",
                    fontFamily: "Clash Grotesk",
                    fontSize: "11px",
                    fontWeight: useSuperpixels ? 600 : 500,
                    color: useSuperpixels ? "#1e293b" : "#64748b",
                    backgroundColor: useSuperpixels ? "#fff" : "transparent",
                    boxShadow: useSuperpixels ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  Smooth
                </button>
                <button
                  onClick={() => {
                    setUseSuperpixels(false);
                    if (window.redetectColorsForAlgorithm) {
                      window.redetectColorsForAlgorithm(false);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: "6px 10px",
                    borderRadius: "6px",
                    border: "none",
                    fontFamily: "Clash Grotesk",
                    fontSize: "11px",
                    fontWeight: !useSuperpixels ? 600 : 500,
                    color: !useSuperpixels ? "#1e293b" : "#64748b",
                    backgroundColor: !useSuperpixels ? "#fff" : "transparent",
                    boxShadow: !useSuperpixels ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  Detailed
                </button>
              </div>
            </div>

            {/* Divider */}
            <div style={{ height: "1px", backgroundColor: "rgba(0,0,0,0.05)", margin: "4px 0 12px" }} />

            {/* Sliders */}
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <CustomSlider
                label="Smoothness"
                value={smoothness}
                min={0}
                max={1.34}
                step={0.01}
                onChange={(value) => setSmoothness(value)}
              />
              
              <CustomSlider
                label="Despeckle"
                min={0}
                max={imageDimensions && imageDimensions[0] / 4 > 20 ? imageDimensions[0] / 4 : 20}
                value={despeckle}
                step={1}
                onChange={(value) => setDespeckle(value)}
              />
              
              <CustomSlider
                label="Simplify Curves"
                value={simplifyCurves}
                min={0}
                max={1000}
                onChange={(value) => setSimplifyCurves(value)}
                step={0.01}
              />
            </div>

            {/* Divider */}
            <div style={{ height: "1px", backgroundColor: "rgba(0,0,0,0.05)", margin: "12px 0 10px" }} />

            {/* Morphology toggle */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 10px",
                borderRadius: "8px",
                backgroundColor: useMorphology ? "#eff6ff" : "#f8fafc",
                border: useMorphology ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
              onClick={() => setUseMorphology(!useMorphology)}
            >
              <div>
                <div style={{
                  fontFamily: "Clash Grotesk",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "#1e293b",
                }}>Shape Cleanup</div>
                <div style={{
                  fontFamily: "Clash Grotesk",
                  fontSize: "10px",
                  color: "#64748b",
                  marginTop: "1px",
                }}>Smooths edges & removes noise</div>
              </div>
              <div style={{
                width: "32px",
                height: "18px",
                borderRadius: "9px",
                backgroundColor: useMorphology ? "#3b82f6" : "#cbd5e1",
                position: "relative",
                transition: "all 0.15s",
                flexShrink: 0,
                marginLeft: "10px",
              }}>
                <div style={{
                  width: "14px",
                  height: "14px",
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  position: "absolute",
                  top: "2px",
                  left: useMorphology ? "16px" : "2px",
                  transition: "all 0.15s",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                }} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TraceToolsPopover;
